import React, { useContext, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { avatarBaseCss } from './avatarBaseStyle';
import AvatarGroupContext from './AvatarGroupContext';
import {
  AVATAR_SLOT_TOKEN_KEYS,
  AVATAR_COLORS,
  AVATAR_NEUTRALS,
  AVATAR_SIZE_TOKEN_KEYS,
  AVATAR_SIZES,
  AvatarColor,
  AvatarNeutral,
  AvatarSize,
  DEFAULT_AVATAR_NAME,
  DEFAULT_AVATAR_SUFFIX,
  REMOVED_USER_SUFFIX,
} from './constants';
import getColorByName from './helpers/getColorByName';
import initialsFromName from './helpers/initialsFromName';

const defaultVariants = {
  [AVATAR_SIZES.SMALL_16]: {
    width: '16px',
    height: '16px',
    fontSize: '11px',
  },
  [AVATAR_SIZES.MEDIUM_24]: {
    width: '24px',
    height: '24px',
    fontSize: '12px',
  },
  [AVATAR_SIZES.MEDIUM_32]: {
    width: '32px',
    height: '32px',
    fontSize: '16px',
  },
  [AVATAR_SIZES.LARGE_48]: {
    width: '48px',
    height: '48px',
    fontSize: '24px',
  },
  [AVATAR_SIZES.XLARGE_64]: {
    width: '64px',
    height: '64px',
    fontSize: '32px',
  },
  [AVATAR_SIZES.XXLARGE_128]: {
    width: '128px',
    height: '128px',
    fontSize: '56px',
  },
};

/**
 * Avatars are a visual representation of a customers identity.
 * These may be small thumbnails as part of a menu or more prominent, standalone elements on account and management views.
 * Initials are dynamically generated from the provided name and always rendered. This allows the initials to serve as a placeholder while the image is loading. Read more about when and how to use the Avatar component on the internal wiki.
 */
const Avatar = React.forwardRef<
  HTMLSpanElement,
  StylableComponent<HTMLSpanElement> & {
    name?: string;
    image?: string;
    imageUrl?: string | null;
    size?: AvatarSize;
    color?: AvatarColor | AvatarNeutral;
  }
>(
  (
    {
      style,
      className,
      children,
      name = DEFAULT_AVATAR_NAME,
      image,
      imageUrl,
      color: colorProp,
      size: sizeProp = AVATAR_SIZES.MEDIUM_32,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const [error, setError] = useState(false); // Whether image failed to load
    const [loaded, setLoaded] = useState(false); // Whether image loaded successfully

    let finalImage = imageUrl ?? image; // Support both imageUrl and image same as we use in our api
    if (finalImage && DEFAULT_AVATAR_SUFFIX.test(finalImage)) {
      finalImage = undefined; // remove default Image and show letter avatar instead
    }

    const finalName = useMemo(() => {
      if (name && REMOVED_USER_SUFFIX.test(name)) {
        return name.replace(REMOVED_USER_SUFFIX, ''); // remove any suffix inside parenthesis at the end
      }
      return name;
    }, [name]);

    const color = colorProp ?? getColorByName(finalName);

    const avatarGroup = useContext(AvatarGroupContext);
    const size = avatarGroup ? avatarGroup.size : sizeProp;
    const nameInitials = useMemo(
      () => initialsFromName(finalName, size),
      [finalName, size],
    );

    const avatar = theme.tokens?.component?.avatar;
    const groupBorderColor = avatar?.group?.['border-color'];
    const colorTokens = avatar?.[AVATAR_SLOT_TOKEN_KEYS[color]];
    const sizeTokens =
      avatar?.[AVATAR_SIZE_TOKEN_KEYS[size ?? AVATAR_SIZES.MEDIUM_32]];

    const sizeStyles = {
      ...defaultVariants[size ?? AVATAR_SIZES.MEDIUM_32],
      ...(sizeTokens?.['font-size'] && {
        fontSize: sizeTokens['font-size'],
        lineHeight: sizeTokens['line-height'],
      }),
    };

    const groupStyles = avatarGroup && {
      marginLeft: sizeTokens?.gap,
      boxSizing: 'content-box' as const,
      ...(groupBorderColor && {
        borderWidth: sizeTokens?.['border-width'],
        borderStyle: 'solid',
        borderColor: groupBorderColor,
      }),
    };

    return (
      <span
        {...props}
        className={className}
        ref={ref}
        data-testid="Avatar"
        css={[
          {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            justifyContent: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            verticalAlign: 'middle',
            cursor: 'default',
          },
          avatarBaseCss(avatar?._),
          colorTokens,
          sizeStyles,
          groupStyles,
          style,
        ]}
      >
        {!error && finalImage && (
          <img
            css={[
              {
                width: '100%',
                height: '100%',
                borderRadius: avatar?._?.['border-radius'],
                objectFit: 'cover',
                ...(!loaded && { display: 'none' }), // hide image until loaded
              },
            ]}
            alt={finalName}
            src={finalImage}
            data-testid="Avatar-img"
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
        )}
        {(error || !loaded || !finalImage) &&
          (children || (nameInitials && <span>{nameInitials}</span>))}
      </span>
    );
  },
);

Avatar.displayName = 'Avatar';

Avatar.propTypes = {
  /** Avatar's content when image is missing */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The user full name for the avatar */
  name: PropTypes.string,
  /** Set the size of the avatar */
  size: PropTypes.oneOf(Object.values(AVATAR_SIZES)),
  /** Set the colors of the avatar */
  color: PropTypes.oneOf([
    ...Object.values(AVATAR_COLORS),
    ...Object.values(AVATAR_NEUTRALS),
  ]),
  /** URL to a profile image */
  image: PropTypes.string,
  /** URL to a profile image */
  imageUrl: PropTypes.string,
};

export default Object.assign(Avatar, {
  SIZES: AVATAR_SIZES,
  COLORS: AVATAR_COLORS,
  NEUTRALS: AVATAR_NEUTRALS,
});
